import { useState, type VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { Action } from '../../../../../shared/permissions';
import useHasReleaseGroupPermission from '../../../../../shared/permissions/useHasReleaseGroupPermission';
import useHasReleaseGroupReleasePermission from '../../../../../shared/permissions/useHasReleaseGroupReleasePermission';
import { ButtonGroup, TooltipButton, TooltipNavigationButton } from '../../../../generic/Button';
import FromNow from '../../../../generic/FromNow';
import { icons } from '../../../../generic/Icon';
import List from '../../../../generic/List';
import ConfirmReleaseDelete from '../../components/ConfirmReleaseDelete';
import useDeleteRelease from '../hooks/useDeleteRelease';

type Props = {
  id: number;
  releaseGroupId: number;
  title: string;
  createdAt: Date;
  isOnlyRelease: boolean;
};

/**
 * ReleaseGroupReleasesListRow is a single row in the list of releases,
 * representing a single release.
 */
const ReleaseGroupReleasesListRow: VoidFunctionComponent<Props> = ({
  id,
  releaseGroupId,
  title,
  createdAt,
  isOnlyRelease,
}) => {
  const [isShowingDeleteModal, setIsShowingDeleteModal] = useState(false);
  const { mutateAsync: deleteRelease } = useDeleteRelease(releaseGroupId, id);
  const addToast = useAddToast();

  const hasReleaseCreatePermission = useHasReleaseGroupReleasePermission(releaseGroupId, Action.Create);
  const hasReleaseEditPermission = useHasReleaseGroupReleasePermission(releaseGroupId, Action.Edit);

  const hasReleaseGroupEditPermission = useHasReleaseGroupPermission(releaseGroupId, Action.Edit);

  const onDeleteRelease = async () => {
    try {
      await deleteRelease();
    } catch (_error) {
      addToast({ content: 'Failed to delete release.', type: 'error' });
    }

    setIsShowingDeleteModal(false);
  };

  return (
    <>
      <ConfirmReleaseDelete
        release={{ title }}
        isShowing={isShowingDeleteModal}
        onHide={() => setIsShowingDeleteModal(false)}
        onConfirm={onDeleteRelease}
      />
      <List.RowContainer>
        <List.Row
          hasTightPadding
          isInteractive={false}
          right={
            <ButtonGroup>
              <TooltipNavigationButton
                id={`edit-release-button--${id}`}
                tooltip="You do not have permission to edit releases."
                showTooltip={!hasReleaseEditPermission}
                to={`/projects/group/${releaseGroupId}/releases/${id}/edit`}
                type="secondary"
                size="medium"
                icon={icons.edit}
                testId={`edit-release-button--${id}`}
                isDisabled={!hasReleaseEditPermission}
              >
                Edit
              </TooltipNavigationButton>
              <TooltipNavigationButton
                id={`clone-release-button--${id}`}
                tooltip="You do not have permission to clone releases."
                showTooltip={!hasReleaseCreatePermission}
                to={`/projects/group/${releaseGroupId}/releases/${id}/clone`}
                type="secondary"
                size="medium"
                icon={icons.cloneO}
                testId={`clone-release-button--${id}`}
                isDisabled={!hasReleaseCreatePermission}
              >
                Clone
              </TooltipNavigationButton>
              <TooltipButton
                id={`delete-release-button--${id}`}
                type="error"
                size="medium"
                isDisabled={isOnlyRelease || !hasReleaseGroupEditPermission}
                showTooltip={isOnlyRelease}
                tooltip="The last release cannot be deleted."
                onClick={() => setIsShowingDeleteModal(true)}
              >
                Delete
              </TooltipButton>
            </ButtonGroup>
          }
        >
          <List.RowTitle>{title}</List.RowTitle>
          <List.RowSubtitle>
            <FromNow date={createdAt} />
          </List.RowSubtitle>
        </List.Row>
      </List.RowContainer>
    </>
  );
};

export default ReleaseGroupReleasesListRow;
